import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { Calendar, Tag as TagIcon, ArrowRight, Loader2 } from 'lucide-react';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-CH', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
const readingTime = (body) => `${Math.max(1, Math.ceil((body || '').split(/\s+/).length / 200))} Min. Lesezeit`;

export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(apiBase() + '/api/blog')
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Blog"
        description="Aktuelle Beiträge zu Schweizer Immobilien-Investments, Aktienrecht, Marktanalysen und Investoren-Updates der Löwenherz Invest AG."
        keywords="Blog Schweizer Immobilien, Immobilien-AG News, Aktien Investment Blog, Löwenherz News"
        breadcrumb={[
          { name: 'Home', url: 'https://loewenherz.ag/' },
          { name: 'Blog', url: 'https://loewenherz.ag/blog' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Löwenherz Invest AG Blog',
          url: 'https://loewenherz.ag/blog',
          publisher: {
            '@type': 'Organization',
            name: 'Löwenherz Invest AG',
            logo: { '@type': 'ImageObject', url: 'https://loewenherz.ag/logo.png' },
          },
        }}
      />
      <Header />

      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Hero */}
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C4A55A]">Insights</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 mb-4">
              Löwenherz <span className="text-[#C4A55A]">Blog</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Aktuelle Themen rund um Schweizer Immobilien-Investments, Aktienrecht,
              Marktanalysen und Updates für unsere Investoren.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-400 py-20">
              <Loader2 className="h-5 w-5 animate-spin" /> Lade Beiträge…
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-16 text-center">
              <h2 className="text-xl font-semibold mb-2">Noch keine Beiträge</h2>
              <p className="text-gray-400">Schaue bald wieder vorbei – wir arbeiten an spannenden Inhalten.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden hover:border-[#C4A55A]/40 transition-colors flex flex-col"
                >
                  {p.coverImageUrl ? (
                    <div className="aspect-[16/9] overflow-hidden bg-black/40">
                      <img
                        src={p.coverImageUrl} alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#C4A55A]/20 via-[#0f0f0f] to-black flex items-center justify-center">
                      <span className="text-[#C4A55A]/40 text-6xl font-bold">L</span>
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    {(p.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {p.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-wider text-[#C4A55A] bg-[#C4A55A]/10 border border-[#C4A55A]/20 px-2 py-0.5 rounded-full">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <h2 className="text-lg font-semibold mb-2 group-hover:text-[#C4A55A] transition-colors">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="text-sm text-gray-400 leading-relaxed mb-4 flex-1">{p.excerpt}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-3 border-t border-white/5">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {fmtDate(p.publishedAt || p.createdAt)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#C4A55A] group-hover:gap-2 transition-all">
                        Lesen <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
